import React from 'react';
import PropTypes from 'prop-types';
import Loader from 'relements/build/esm/decorators/loader';
import Button from 'relements/build/esm/ui/button';
import Tabs from 'relements/build/esm/ui/tabs';
import Icon from 'relements/build/esm/ui/icon';
import humps from 'humps';
import { createAlert } from '@src/actions/_utils';
import { AlertTypes } from 'constants/MogamboConstants';

import EntityStore from '@src/stores/EntityStore';
import CommonStore from '@src/stores/CommonStore';
import BotStore from '@src/store';

// Components
import connection from '@src/connection';

// Constants
import {
  ENTITY_TYPES,
  ENTITY_TYPE_TEXT,
  ENTITY_TYPE_NUMERIC_RANGE,
  ENTITY_TYPE_REGEX,
  ENTITY_TYPE_ATTACHMENT,
  ENTITY_TYPE_BUDGET,
  ENTITY_TYPE_DATE,
  ENTITY_TYPE_TIME,
  ENTITY_CATEGORY,
  ENTITY_TYPE_CURRENCY,
} from '@src/constants/Entities';
import { MIX_PANEL } from '@src/const';

// Local
import EntityInfo from './EntityInfo';
import DateEntityConfig from './DateEntityConfig';
import NumericRangeEntityConfig from './NumericRangeEntityConfig';
import RegexEntityConfig from './RegexEntityConfig';
import BudgetEntityConfig from './BudgetEntityConfig';
import CurrencyEntityConfig from './CurrencyEntityConfig';
import TimeEntityConfig from './TimeEntityConfig';
import EntityPatterns from './EntityPatterns';
import AttachmentEntityConfig from './AttachmentEntityConfig';
import EntityCopyModal from '../EntitySearch/EntityCopyModal';

import Dictionary from './Dictionary';

import * as maps from './maps';
import styles from './EntityEditor.scss';
import { isValidRegex } from './utils';

export const DEFAULTS = {
  CHANNEL_ID: '5',
  CHANNEL_NAME: 'WebSDK',
};

const TABS = [
  {
    title: 'Entity Info',
    path: 'entity_info',
  },
  {
    title: 'Dictionary',
    path: ENTITY_TYPE_TEXT,
  },
  {
    title: 'Entity Patterns',
    path: 'undefined_text',
  },
  {
    title: 'Range',
    path: ENTITY_TYPE_NUMERIC_RANGE,
  },
  {
    title: 'Attachment',
    path: ENTITY_TYPE_ATTACHMENT,
  },
  {
    title: 'Range',
    path: ENTITY_TYPE_BUDGET,
  },
  {
    title: 'How it Works',
    path: ENTITY_TYPE_CURRENCY,
  },
  {
    title: 'Regex Pattern',
    path: ENTITY_TYPE_REGEX,
  },
  {
    title: 'How It Works',
    path: ENTITY_TYPE_DATE,
  },
  {
    title: 'How It Works',
    path: ENTITY_TYPE_TIME,
  },
];

@Loader({})
@connection(maps.listenToStores, maps.getStateFromStores)
export default class EntityEditor extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      currentTab: 'entity_info',
      name: '',
      id: null,
      description: '',
      defaultResponses: {},
      defaultBotResponsesWithChannel: {},
      disableTranslation: false,
      payloadKey: '',
      contextKey: '',
      formKey: '',
      prevContextTags: '',
      entityType: ENTITY_TYPES[0],
      entityWordInfo: [],
      entityCategory: ENTITY_CATEGORY.DOMAIN,
      entityDomainName: props.nodeDomainName,
      metaData: {},
      crfData: {},
      isNewState: props.new,
      enabledLanguages: [],
      copyModalActive: false,
      crfDataEdited: false,
      dpsvChannels: [],
      isBotResponsesEdited: false,
      isBotResponsesReset: false,
      currentChannelName: DEFAULTS.CHANNEL_NAME,
    };
  }

  componentDidMount() {
    if (this.props.entityName) {
      this.props.activateLoader();
      setTimeout(
        () => maps.fetchEntityData(this.props.entityName, this.props.entityId),
        0
      );
    }
    setTimeout(() => {
      maps.updateComponentModal({
        onIconClick: this.onClose,
      });
    }, 0);
  }

  componentWillUnmount() {
    maps.clearEntityData();
  }

  componentDidUpdate(prevProps, prevState) {
    // If any change has been made => Update the header buttons to make the Save button active
    if (prevState.edited !== this.state.edited) {
      this.updateModalHeaderButtons();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.props.entityDataLoading &&
      !nextProps.entityDataLoading &&
      this.props.loaderActive
    ) {
      this.props.deactivateLoader();

      const convertedEntityCategory = maps.convertEntityCategory(
        nextProps.entityData.entityCategory,
        nextProps.entityData.domainName,
        nextProps.nodeDomainName
      );

      this.setState({
        name: nextProps.entityData.name,
        personalData: nextProps.entityData.personalData,
        id: this.state.isNewState ? null : nextProps.entityData.id,
        description: nextProps.entityData.description,
        defaultResponses: nextProps.entityData.defaultResponses,
        disableTranslation: nextProps.entityData.disableTranslation,
        payloadKey: nextProps.entityData.apiName,
        contextKey: nextProps.entityData.completionKey,
        formKey: nextProps.entityData.formKey,
        prevContextTags: nextProps.entityData.prevContextTags,
        entityType: nextProps.entityData.entityType,
        entityWordInfo: nextProps.entityData.entityWordInfo,
        entityCategory: nextProps.new
          ? ENTITY_CATEGORY.DOMAIN
          : convertedEntityCategory,
        entityDomainName: nextProps.new
          ? nextProps.nodeDomainName
          : nextProps.entityData.domainName ||
            CommonStore.getMetaTags().domain.title,
        metaData: nextProps.entityData.metaData,
        crfData: nextProps.entityData.crfData,
        edited: nextProps.new,
        enabledLanguages: nextProps.entityData.supportedLanguages,
      });
      this.updateModalHeaderButtons(nextProps.entityData.name);
    } else if (nextProps.entityName !== this.props.entityName) {
      this.props.activateLoader();
      setTimeout(
        () => maps.fetchEntityData(nextProps.entityName, nextProps.entityId),
        0
      );
    }

    // Mixpanel Event for Reset of Responses
    const {
      currentBotSaysLanguage: language,
      currentHybridLanguage: hybridLanguage,
      entityData,
    } = this.props;
    const {
      currentBotSaysLanguage: nextLanguage,
      currentHybridLanguage: nextHybridLanguage,
      entityData: nextEntityData,
    } = nextProps;

    if (language === nextLanguage && hybridLanguage === nextHybridLanguage) {
      const currentDevicePlatformMap =
        entityData?.defaultResponses?.[language]?.[hybridLanguage]
          ?.devicePlatformMap;
      const newCurrentDevicePlatformMap =
        nextEntityData?.defaultResponses?.[nextLanguage]?.[nextHybridLanguage]
          ?.devicePlatformMap;
      const currentChannels =
        currentDevicePlatformMap && Object.keys(currentDevicePlatformMap);
      const nextChannels =
        newCurrentDevicePlatformMap && Object.keys(newCurrentDevicePlatformMap);
      if (
        currentChannels &&
        nextChannels &&
        currentChannels.length !== nextChannels.length
      ) {
        currentChannels.forEach((channelId) => {
          if (!nextChannels.includes(channelId)) {
            const { currentChannelName } = this.state;
            const mixPanelTracker = BotStore.getMixPanel();
            const meta = CommonStore.getMetaTags();
            const business = BotStore.getBusinessesLinkedToBot()?.[0];

            this.setState({
              isBotResponsesReset: true,
            });

            mixPanelTracker.track({
              event: MIX_PANEL.OMNICHANNEL.RESET,
              payload: {
                'Bot Name': meta?.domain?.title,
                'Bot Account': meta?.domain?.client,
                'Business ID': business?.id,
                'Business Name': business?.via_name,
                'Channel Name': currentChannelName,
              },
            });
          }
        });
      }
    }

    const currentBotSaysLanguage = EntityStore.getCurrentBotSaysLanguage();
    const currentHybridLanguage = EntityStore.getCurrentHybridLanguage();
    let botResponsesWithChannel;
    if (!this.state.edited) {
      botResponsesWithChannel = this.updateBotResponesWithChannel(
        nextProps?.entityData?.defaultResponses,
        currentBotSaysLanguage,
        currentHybridLanguage
      );
    } else {
      botResponsesWithChannel = this.updateBotResponesWithChannel(
        this.state.defaultResponses,
        currentBotSaysLanguage,
        currentHybridLanguage
      );
    }

    this.setState({
      defaultBotResponsesWithChannel: botResponsesWithChannel,
      dpsvChannels: Object.keys(
        nextProps.entityData?.defaultResponses?.[currentBotSaysLanguage]?.[
          currentHybridLanguage
        ]?.devicePlatformMap || {}
      ),
    });
  }

  updateCurrentChannelName = (channelName) => {
    this.setState({
      currentChannelName: channelName,
    });
  };

  updateBotResponesWithChannel = (botResponses, language, hybridLanguage) => {
    let botResponsesWithChannel = {
      [language]: {},
    };
    const platformMap =
      botResponses?.[language]?.[hybridLanguage]?.devicePlatformMap;

    const variants = botResponses?.[language]?.[hybridLanguage]?.responses;

    if (platformMap && variants) {
      const channels = Object.keys(platformMap || {});

      if (channels.length === 0) {
        channels.push(DEFAULTS.CHANNEL_ID);
      }

      channels.forEach((channelId) => {
        const botResponsesDeviceMap = platformMap?.[channelId];
        let botResponsesChannelVariants = [];

        variants.forEach((channelItem, index) => {
          if (botResponsesDeviceMap?.includes(index)) {
            botResponsesChannelVariants.push(channelItem);
          }
        });

        const botResponsesDeviceMapWithChannel =
          botResponsesDeviceMap !== undefined
            ? {
                [channelId]: botResponsesDeviceMap,
              }
            : {};

        botResponsesChannelVariants =
          botResponsesChannelVariants.length > 0
            ? botResponsesChannelVariants
            : variants;

        botResponsesWithChannel = {
          [language]: {
            ...botResponsesWithChannel[language],
            [channelId]: {
              ...botResponsesWithChannel[language][hybridLanguage],
              channelDataUpdated: true,
              botSays: {
                devicePlatformMap: botResponsesDeviceMapWithChannel,
                quickResponses:
                  botResponses?.[language][hybridLanguage]?.quickResponses,
                variants: botResponsesChannelVariants,
              },
            },
          },
        };
      });
    }

    return botResponsesWithChannel;
  };

  updateModalHeaderButtons = (title = null) => {
    setTimeout(() => {
      // Save Button
      // Show only if Local for current bot / New Entity
      // If edited changes => Active
      // If no edited Changes => Inactive
      const { entityCategory, entityType } = this.state;
      const saveButtonStatus = {
        visible: entityCategory === ENTITY_CATEGORY.DOMAIN,
        disabled: !this.state.edited,
        text: 'Save',
      };

      // Add to Bot Says:
      // Show only for Public / Local Entities
      // Show Already Present/Disabled if added on Node
      // Else Add to Bot Says action and close modal
      const entityPresentOnNode = this.props.entitiesOnNode.includes(
        this.state.name
      );
      const addToBotSaysStatus = {
        visible: [ENTITY_CATEGORY.SYSTEM, ENTITY_CATEGORY.DOMAIN].includes(
          entityCategory
        ),
        disabled: this.state.edited || entityPresentOnNode,
        text: entityPresentOnNode
          ? 'Already Present on Step'
          : 'Add to Bot Responses',
      };

      // Copy Button : 'Add to Bot Says'
      // Show only for Local (Other Bot) / Deprecated entities
      // Always active
      const copyButtonVisible =
        this.props.onCopyEntity &&
        [ENTITY_CATEGORY.DEPRECATED].includes(entityCategory) &&
        ENTITY_TYPES.includes(entityType);
      const copyButtonStatus = {
        visible: copyButtonVisible,
        disabled: false,
        text: 'Copy as Local Entity',
      };

      const changesToUpdate = {
        headerRight: this.generateHeaderActions(
          saveButtonStatus,
          addToBotSaysStatus,
          copyButtonStatus
        ),
      };
      if (title) changesToUpdate.title = title;
      maps.updateComponentModal(changesToUpdate);
    }, 0);
  };

  generateHeaderActions = (
    saveButtonStatus,
    addToBotSaysStatus,
    copyButtonStatus
  ) => {
    return (
      <div className={styles.modalActionables}>
        {copyButtonStatus.visible && (
          <Button
            disabled={copyButtonStatus.disabled}
            onClick={this.createCopy}
            type={Button.TYPES.PRIMARY}
          >
            {copyButtonStatus.text}
          </Button>
        )}
        {addToBotSaysStatus.visible && this.props.onAddEntityToNode ? (
          <Button
            disabled={addToBotSaysStatus.disabled}
            onClick={this.onAddToBotSays}
            type={Button.TYPES.PRIMARY}
          >
            {addToBotSaysStatus.text}
          </Button>
        ) : null}
        {saveButtonStatus.visible && (
          <Button
            disabled={saveButtonStatus.disabled}
            onClick={this.onSave}
            type={Button.TYPES.PRIMARY}
          >
            {saveButtonStatus.text}
          </Button>
        )}
      </div>
    );
  };

  render() {
    const { currentTab, entityType, name, enabledLanguages } = this.state;
    const RENDER_TABS = this.getTabs();
    const entityInfoTab = this.renderEntityInfo();
    const undefinedTextConfigTab = this.renderUndefinedTextConfig();
    let entityConfigTab = null;

    switch (entityType) {
      case ENTITY_TYPE_TEXT:
        entityConfigTab = this.renderTextEntityConfig();
        break;

      case ENTITY_TYPE_NUMERIC_RANGE:
        entityConfigTab = this.renderNumericRangeEntityConfig();
        break;

      case ENTITY_TYPE_REGEX:
        entityConfigTab = this.renderRegexEntityConfig();
        break;

      case ENTITY_TYPE_BUDGET:
        entityConfigTab = this.renderBudgetEntityConfig();
        break;

      case ENTITY_TYPE_DATE:
        entityConfigTab = this.renderDateEntityConfig();
        break;

      case ENTITY_TYPE_TIME:
        entityConfigTab = this.renderTimeEntityConfig();
        break;

      case ENTITY_TYPE_ATTACHMENT:
        entityConfigTab = this.renderAttachmentEntityConfig();
        break;

      case ENTITY_TYPE_CURRENCY:
        entityConfigTab = this.renderCurrencyEntityConfig();
        break;

      default:
        break;
    }

    return (
      <>
        {!this.canEdit() ? (
          <div className={styles.alertWarning}>
            <Icon src="error_response" />
            &nbsp;&nbsp;
            {this.props.alert
              ? this.props.alert
              : 'Entities other than local are not editable.'}
          </div>
        ) : null}
        <div className={styles.entityEditorContainer}>
          <Tabs
            className={styles.entityEditorContainerTabs}
            value={this.state.currentTab}
            items={RENDER_TABS}
            key={this.state.entityType}
          >
            {RENDER_TABS.map((tab) => (
              <Tabs.Item value={tab.path} onClick={this.navToTab}>
                {tab.title}
              </Tabs.Item>
            ))}
          </Tabs>
          {currentTab === 'entity_info' ? (
            <div className={styles.bodyContainer}>{entityInfoTab}</div>
          ) : null}
          {currentTab === 'undefined_text' ? (
            <div className={styles.bodyContainer}>{undefinedTextConfigTab}</div>
          ) : null}
          {currentTab === entityType ? (
            <div className={styles.bodyContainer}>{entityConfigTab}</div>
          ) : null}
          <EntityCopyModal
            onCopy={this.copyEntity}
            onClose={() => this.setState({ copyModalActive: false })}
            active={this.state.copyModalActive}
            entityToCopy={{
              supported_languages: enabledLanguages,
              entity_type: entityType,
              name,
            }}
          />
        </div>
      </>
    );
  }

  onSave = () => {
    if (!this.validate(null, true)) {
      return;
    }

    const cleanedData = this.cleanedData();
    this.props.activateLoader();
    maps.saveEntityData(cleanedData, (success, data) => {
      if (success) {
        const { currentBotSaysLanguage, currentHybridLanguage } = this.props;
        this.setState({
          edited: false,
          isNewState: false,
          id: data.id,
          dpsvChannels: Object.keys(
            this.state.defaultResponses?.[currentBotSaysLanguage]?.[
              currentHybridLanguage
            ]?.devicePlatformMap || {}
          ),
        });
        if (this.state.crfDataEdited) {
          this.setState({
            crfDataEdited: false,
          });
        }

        if (
          this.dictionaryRef &&
          typeof this.dictionaryRef.reset === 'function'
        ) {
          this.dictionaryRef.reset();
        }

        // Mixpanel Event for Add Responses
        const {
          isBotResponsesEdited,
          currentChannelName,
          isBotResponsesReset,
        } = this.state;
        if (isBotResponsesEdited && !isBotResponsesReset) {
          const mixPanelTracker = BotStore.getMixPanel();
          const meta = CommonStore.getMetaTags();
          const business = BotStore.getBusinessesLinkedToBot()?.[0];
          mixPanelTracker.track({
            event: MIX_PANEL.OMNICHANNEL.ACTIVITY,
            payload: {
              'Bot Name': meta?.domain?.title,
              'Bot Account': meta?.domain?.client,
              'Business ID': business?.id,
              'Business Name': business?.via_name,
              'Channel Name': currentChannelName,
            },
          });
        }
        this.setState({
          isBotResponsesEdited: false,
          isBotResponsesReset: false,
        });
      }
      this.props.deactivateLoader();
    });
  };

  validate = (ignoreTab, skipDictionary, isTabSwitched) => {
    // If entities are not editable, then no need to check for validations
    if (!this.canEdit()) {
      return true;
    }

    if (
      ignoreTab !== 'entity_info' &&
      this.entityInfoTabRef &&
      this.entityInfoTabRef.validate
    ) {
      if (!this.entityInfoTabRef.validate()) {
        this.setState({ currentTab: 'entity_info' });
        return false;
      }
    }

    if (
      ignoreTab !== ENTITY_TYPE_TEXT &&
      this.dictionaryRef &&
      this.dictionaryRef.validate &&
      !skipDictionary
    ) {
      if (
        !this.dictionaryRef.validate(() => {
          this.setState({ currentTab: ignoreTab });
        })
      ) {
        return false;
      }
    } else if (
      ignoreTab !== this.state.entityType &&
      this._entityTypeConfigRef &&
      this._entityTypeConfigRef.validate
    ) {
      if (!this._entityTypeConfigRef.validate()) {
        this.setState({ currentTab: this.state.entityType });
        return false;
      }
    }

    if (
      !isTabSwitched &&
      this.state.currentTab === 'entity_info' &&
      this.state.entityType === ENTITY_TYPE_REGEX &&
      this.state.metaData.regex
    ) {
      if (!isValidRegex(this.state.metaData.regex)) {
        createAlert(
          'Invalid regular expression',
          undefined,
          AlertTypes.NEGATIVE
        );
        return false;
      }
    }

    return true;
  };

  cleanedData = () => {
    const name = this.state.name.trim();
    const entityWordInfo = (this.state.entityWordInfo || []).filter((row) => {
      row.value = row.value.trim();
      row.variants = row.variants.map((i) => i.trim());
      return row.value && row.variants.length > 0;
    });
    // backend needs a key to verify if number_range entity has unit type currency
    const metaData =
      this.state.entityType === 'number_range'
        ? { ...this.state.metaData, unit_type: 'currency' }
        : this.state.metaData;

    return {
      name,
      personalData: this.state.personalData,
      id: this.state.id,
      athenaName: this.state.name,
      entityType: this.state.entityType,
      description: this.state.description,
      disableTranslation: this.state.disableTranslation,
      formKey: this.state.formKey,
      prevContextTags: this.state.prevContextTags,
      apiName: this.state.payloadKey,
      completionKey: this.state.contextKey,
      dictionaryName: this.state.name,
      entityWordInfo,
      metaData,
      crfData: this.state.crfData,
      entityCategory: this.state.entityCategory,
      domainName: this.state.entityDomainName,
      defaultResponses: this.state.defaultResponses,
      dictionary: this.state.dictionary,
      supportedLanguages: this.state.enabledLanguages,
      crfDataEdited: this.state.crfDataEdited,
    };
  };

  onAddToBotSays = () => {
    this.props.onAddEntityToNode(
      this.props.entityData.name,
      this.props.entityData.id,
      this.props.entityData.quickResponses || [],
      this.props.entityData.responses || []
    );
    maps.closeComponentModal();
  };

  createCopy = () => {
    this.setState({
      copyModalActive: true,
    });
  };

  canEdit = () => {
    const { isProduction } = this.props;
    const { entityCategory, isNewState } = this.state;
    // Allow entity edit only for those which are in edit mode and belong to the current domain
    return (
      !isProduction && (entityCategory === ENTITY_CATEGORY.DOMAIN || isNewState)
    );
  };

  renderEntityInfo = () => {
    const defaultResponses = this.state.defaultResponses || {};
    const defaultResponsesWithChannel =
      this.state.defaultBotResponsesWithChannel || {};

    return (
      <EntityInfo
        ref={(ref) => {
          if (ref) this.entityInfoTabRef = ref;
        }}
        isSuperman={this.props.isSuperman}
        new={this.state.isNewState}
        edit={this.canEdit()}
        onEntityInfoChange={this._updateEntityInfo}
        onEntityTypeChange={this._updateEntityType}
        name={this.state.name}
        description={this.state.description}
        payloadKey={this.state.payloadKey}
        disableTranslation={this.state.disableTranslation}
        entityCategory={this.state.entityCategory}
        contextKey={this.state.contextKey}
        formKey={this.state.formKey}
        prevContextTags={this.state.prevContextTags}
        entityType={this.state.entityType}
        personalData={this.state.personalData}
        existingEntityList={[]}
        entityRepromptValue={defaultResponses}
        entityRepromptValueWithChannel={defaultResponsesWithChannel}
        domainName={this.state.entityDomainName}
        supermanSupportedLanguages={this.props.supermanSupportedLanguages}
        allHybridLanguages={this.props.allHybridLanguages}
        onEntityRepromptChange={(...args) => {
          if (!this.canEdit()) return;
          this.updateBotSays(...args);
        }}
        onTranslation={this.updateBotResponesWithChannelAfterTranslation}
        isEdited={this.state.edited}
        dpsvChannels={this.state.dpsvChannels}
        updateCurrentChannelName={this.updateCurrentChannelName}
      />
    );
  };

  renderTextEntityConfig = () => {
    return (
      <Dictionary
        customRef={(ref) => {
          if (ref) this.dictionaryRef = ref;
        }}
        disabled={!this.canEdit()}
        enabledLanguages={this.state.enabledLanguages}
        onAddLanguage={this._enableLanguage}
        entityName={this.state.name}
        entityId={this.state.id}
        editable={this.canEdit()}
        new={this.state.isNewState}
        onChange={(dictionary) => {
          this.setState({
            dictionary,
            edited: true,
          });
        }}
      />
    );
  };

  renderNumericRangeEntityConfig = () => {
    return (
      <NumericRangeEntityConfig
        ref={(ref) => {
          if (ref) this._entityTypeConfigRef = ref;
        }}
        disabled={!this.canEdit()}
        metaData={this.state.metaData}
        onChange={(metaData) => {
          this.setState({
            metaData,
            edited: true,
          });
        }}
      />
    );
  };

  renderAttachmentEntityConfig = () => {
    return (
      <AttachmentEntityConfig
        ref={(ref) => {
          if (ref) this._entityTypeConfigRef = ref;
        }}
        disabled={!this.canEdit()}
        metaData={this.state.metaData}
        onChange={(metaData) => {
          this.setState({
            metaData,
            edited: true,
          });
        }}
      />
    );
  };

  renderCurrencyEntityConfig = () => {
    return (
      <CurrencyEntityConfig
        ref={(ref) => {
          if (ref) this._entityTypeConfigRef = ref;
        }}
      />
    );
  };

  renderRegexEntityConfig = () => {
    return (
      <RegexEntityConfig
        ref={(ref) => {
          if (ref) this._entityTypeConfigRef = ref;
        }}
        disabled={!this.canEdit()}
        metaData={this.state.metaData}
        onChange={(metaData) => {
          this.setState({
            metaData,
            edited: true,
          });
        }}
      />
    );
  };

  renderBudgetEntityConfig = () => {
    return (
      <BudgetEntityConfig
        ref={(ref) => {
          if (ref) this._entityTypeConfigRef = ref;
        }}
        disabled={!this.canEdit()}
        metaData={this.state.metaData}
        onChange={(metaData) => {
          this.setState({
            metaData,
            edited: true,
          });
        }}
      />
    );
  };

  renderDateEntityConfig = () => {
    return (
      <DateEntityConfig
        ref={(ref) => {
          if (ref) this._entityTypeConfigRef = ref;
        }}
      />
    );
  };

  renderTimeEntityConfig = () => {
    return (
      <TimeEntityConfig
        ref={(ref) => {
          if (ref) this._entityTypeConfigRef = ref;
        }}
      />
    );
  };

  renderUndefinedTextConfig = () => {
    return (
      <EntityPatterns
        crfData={this.state.crfData}
        entityName={this.state.name}
        entityId={this.state.id}
        canEdit={this.canEdit()}
        onChange={(crfData) => {
          this.setState({
            crfData,
            edited: true,
            crfDataEdited: true,
          });
        }}
        ref={(ref) => {
          if (ref) this._entityTypeConfigRef = ref;
        }}
      />
    );
  };

  updateBotResponesWithChannelAfterTranslation = (
    data,
    currentChannelId,
    cb
  ) => {
    const channelData = humps.camelizeKeys({ ...data });

    const botResponsesWithChannel = structuredClone({
      ...this.state.defaultBotResponsesWithChannel,
    });

    botResponsesWithChannel[EntityStore.getCurrentBotSaysLanguage()] = {
      ...botResponsesWithChannel[EntityStore.getCurrentBotSaysLanguage()],
      [currentChannelId]: {
        botSays: channelData.botSays,
      },
    };

    this.setState({
      defaultBotResponsesWithChannel: botResponsesWithChannel,
    });

    if (cb) cb();
  };

  updateBotSays = (key, value, language, hybridLanguage, currentChannelId) => {
    const { defaultResponses, defaultBotResponsesWithChannel } = this.state;

    let newDefaultResponses = {
      ...defaultResponses,
      [language]: {
        ...defaultResponses?.[language],
        [hybridLanguage]: {
          ...defaultResponses?.[language]?.[hybridLanguage],
          [key]: value,
          devicePlatformMap: {
            ...defaultResponses?.[language]?.[hybridLanguage]
              ?.devicePlatformMap,
            [currentChannelId]: [0],
          },
        },
      },
    };
    const initialChannelId =
      Object.keys(
        newDefaultResponses?.[language]?.[hybridLanguage]?.devicePlatformMap ||
          {}
      ) || [];

    let newBotResponsesWithChannel = { ...defaultBotResponsesWithChannel };
    let filteredBotResponsesWithChannel = {};
    initialChannelId.forEach((channelId) => {
      filteredBotResponsesWithChannel = {
        ...newBotResponsesWithChannel,
        [language]: {
          ...filteredBotResponsesWithChannel[language],
          [channelId]: newBotResponsesWithChannel?.[language]?.[channelId],
        },
      };
    });
    newBotResponsesWithChannel = filteredBotResponsesWithChannel;

    newBotResponsesWithChannel = {
      ...newBotResponsesWithChannel,
      [language]: {
        ...newBotResponsesWithChannel?.[language],
        [currentChannelId]: {
          ...newBotResponsesWithChannel?.[language]?.[currentChannelId],
          botSays: {
            ...newBotResponsesWithChannel?.[language]?.[currentChannelId]
              ?.botSays,
            [key === 'responses' ? 'variants' : key]: value,
            devicePlatformMap:
              newDefaultResponses?.[language]?.[hybridLanguage]
                ?.devicePlatformMap,
          },
        },
      },
    };

    if (value.length === 0) {
      delete newBotResponsesWithChannel[language][currentChannelId];
    }

    const channelIdList = Object.keys(
      newBotResponsesWithChannel[EntityStore.getCurrentBotSaysLanguage()]
    );

    let devicePlatformMap = {};
    let variants = [];
    let currentIndex = 0;
    channelIdList.forEach((channelId) => {
      devicePlatformMap = {
        ...devicePlatformMap,
        [channelId]: newBotResponsesWithChannel[
          EntityStore.getCurrentBotSaysLanguage()
        ]?.[channelId]?.botSays?.variants.map(() => currentIndex++),
      };
      variants = [
        ...variants,
        ...newBotResponsesWithChannel[
          EntityStore.getCurrentBotSaysLanguage()
        ]?.[channelId]?.botSays?.variants,
      ];
    });

    newDefaultResponses = {
      ...newDefaultResponses,
      [EntityStore.getCurrentBotSaysLanguage()]: {
        ...newDefaultResponses[EntityStore.getCurrentBotSaysLanguage()],
        [EntityStore.getCurrentHybridLanguage()]: {
          ...newDefaultResponses?.[EntityStore.getCurrentBotSaysLanguage()]?.[
            EntityStore.getCurrentHybridLanguage()
          ],
          responses: variants,
          devicePlatformMap,
        },
      },
    };

    if (key === 'responses') {
      this.setState({
        isBotResponsesEdited: true,
      });
    }

    this.setState({
      defaultResponses: newDefaultResponses,
      edited: true,
      defaultBotResponsesWithChannel: newBotResponsesWithChannel,
    });
  };

  _updateEntityInfo = (key, value) => {
    this.setState({
      [key]: value,
      edited: true,
    });
  };

  _updateEntityType = (entityType) => {
    this.setState({
      entityType,
      entityWordInfo: [],
      metaData: {},
      edited: true,
    });
  };

  navToTab = (path) => {
    const url = Array.isArray(path) ? path[path.length - 1] : path;
    if (this.validate(url, undefined, true)) {
      if (url === 'entity_info') {
        const responseWithChannel = this.updateBotResponesWithChannel(
          this.state.defaultResponses,
          'en',
          'en'
        );
        this.setState({
          currentTab: url,
          defaultBotResponsesWithChannel: responseWithChannel,
        });
      } else {
        this.setState({
          currentTab: url,
        });
      }
    }
  };

  getTabs = () => {
    return TABS.filter((tab) => {
      if (this.state.entityType !== ENTITY_TYPE_TEXT) {
        return ['entity_info', this.state.entityType].includes(tab.path);
      }
      return ['entity_info', 'undefined_text', this.state.entityType].includes(
        tab.path
      );
    });
  };

  _nextTab = () => {
    const RENDER_TABS = this.getTabs();
    const { currentTab } = this.state;
    RENDER_TABS.map((tab, i) => {
      if (tab.path === currentTab) {
        let newIndex = i + 1;
        newIndex = newIndex >= RENDER_TABS.length ? 0 : newIndex;
        this.navToTab(RENDER_TABS[newIndex].path);
      }
    });
  };

  _prevTab = () => {
    const RENDER_TABS = this.getTabs();
    const { currentTab } = this.state;
    RENDER_TABS.map((tab, i) => {
      if (tab.path === currentTab) {
        let newIndex = i - 1;
        newIndex = newIndex >= 0 ? newIndex : RENDER_TABS.length - 1;
        this.navToTab(RENDER_TABS[newIndex].path);
      }
    });
  };

  onClose = () => {
    if (this.state.edited) {
      maps.showConfirmationDialog({
        okTitle: "Don't Save",
        cancelTitle: 'Cancel',
        title: 'Your changes might get lost!',
        message: 'Are you sure you want to exit without saving?',
        onOk: () => {
          maps.closeComponentModal();
        },
        okActionType: 'danger',
      });
    } else {
      maps.closeComponentModal();
    }
  };

  _enableLanguage = (languageCode) => {
    const { enabledLanguages } = this.state;

    if (enabledLanguages.indexOf(languageCode) < 0) {
      this.setState({
        enabledLanguages: enabledLanguages.concat([languageCode]),
      });
    }
  };

  copyEntity = async ({ srcName, dstName, languageList }) => {
    this.props.activateLoader();
    this.props.onCopyEntity(srcName, dstName, languageList, () => {
      this.props.deactivateLoader();
    });
  };
}

EntityEditor.propTypes = {
  isProduction: PropTypes.bool,
  activateLoader: PropTypes.func,
  deactivateLoader: PropTypes.func,
  loaderActive: PropTypes.bool,
  entityDataLoading: PropTypes.bool,
  nodeDomainName: PropTypes.string,
  entityName: PropTypes.string,
  new: PropTypes.bool,
  entitiesOnNode: PropTypes.arrayOf(PropTypes.string),
  onAddEntityToNode: PropTypes.func,
  onCopyEntity: PropTypes.func,
  entityData: PropTypes.shape({
    name: PropTypes.string,
    personalData: PropTypes.bool,
    id: PropTypes.string,
    description: PropTypes.string,
    entityType: PropTypes.string,
    responses: PropTypes.arrayOf(PropTypes.string),
    quickResponses: PropTypes.arrayOf(PropTypes.string),
    apiName: PropTypes.string,
    disableTranslation: PropTypes.bool,
    completionKey: PropTypes.string,
    formKey: PropTypes.string,
    prevContextTags: PropTypes.string,
    entityWordInfo: PropTypes.arrayOf(
      PropTypes.shape({
        variants: PropTypes.arrayOf(PropTypes.string),
        value: PropTypes.arrayOf(PropTypes.string),
      })
    ),
    domainName: PropTypes.string,
    entityCategory: PropTypes.string,
    metaData: PropTypes.object,
    crfData: PropTypes.object,
    supportedLanguages: PropTypes.array,
    alert: PropTypes.string,
  }),
  supermanSupportedLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      path: PropTypes.string,
    })
  ),
};

EntityEditor.defaultProps = {
  onAddEntityToNode: null,
  onCopyEntity: null,
  supermanSupportedLanguages: [],
};
